import React, { useState } from 'react'
import { invalidateQuery, FC } from '../../Services'
import { DateTime } from 'luxon'
import Stack from '@mui/material/Stack'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Chip from '@mui/material/Chip'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import Divider from '@mui/material/Divider'
import Tooltip from '@mui/material/Tooltip'
import Badge from '@mui/material/Badge'
import InputAdornment from '@mui/material/InputAdornment'
import Typography from '@mui/material/Typography'
import Autocomplete from '@mui/material/Autocomplete'
import SearchIcon from '@mui/icons-material/Search'
import BuildIcon from '@mui/icons-material/Build'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import BusinessIcon from '@mui/icons-material/Business'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'

export const ProcessesFilters = ({
  dateRange,
  setDateRange,
  contentSearch,
  setContentSearch,
  onlyErrors,
  setOnlyErrors,
  onlyDuplicates,
  onToggleOnlyDuplicates,
  onSearch,
  selectedRows,
  setSelectedRows,
  agencies,
  processes,
  agencyFilterId,
  setAgencyFilterId
}) => {
  const [reprocessing, setReprocessing] = useState(false)
  const [reprocessConfirmOpen, setReprocessConfirmOpen] = useState(false)

  const handleReprocessSelected = async () => {
    const rows = selectedRows || []
    if (!rows.length || reprocessing) return

    setReprocessing(true)
    window.growl?.show({
      severity: 'info',
      summary: 'Riprocesso avviato',
      detail: `Elaborazione di ${rows.length} file in corso...`
    })

    try {
      const results = await Promise.allSettled(rows.map(async (row) => {
        const agency = agencies.find(a => a.agencyId === row.agencyId)
        if (!agency) throw new Error(`Agenzia non trovata per ${row.fileName || row._id}`)
        await FC.service('processes').patch(row._id, { ...row, serverId: agency.serverId, reprocess: true })
      }))

      const failed = results.filter(r => r.status === 'rejected')
      if (failed.length) console.error('Reprocess errors:', failed)
      const ok = rows.length - failed.length

      if (!failed.length) {
        window.growl?.show({ severity: 'success', summary: 'Riprocesso completato', detail: `${ok} file riprocessati.` })
      } else if (ok) {
        window.growl?.show({ severity: 'warning', summary: 'Riprocesso parziale', detail: `${ok} completati, ${failed.length} falliti.` })
      } else {
        window.growl?.show({ severity: 'error', summary: 'Riprocesso fallito', detail: `${failed.length} file non riprocessati.` })
      }

      invalidateQuery(['processesPage', 'processesErrorCount'])
      setSelectedRows([])
      setReprocessConfirmOpen(false)
    } finally {
      setReprocessing(false)
    }
  }

  const selectedCount = selectedRows?.length || 0
  const previewFileNames = (selectedRows || []).slice(0, 5).map(r => r.fileName).filter(Boolean)

  const startDate = dateRange?.[0] ? DateTime.fromJSDate(dateRange[0]) : null
  const endDate = dateRange?.[1] ? DateTime.fromJSDate(dateRange[1]) : null

  const handleStartDateChange = (newValue) => {
    if (!newValue) return
    const start = newValue.toJSDate()
    const end = dateRange?.[1] || new Date()
    setDateRange([start, end])
  }

  const handleEndDateChange = (newValue) => {
    if (!newValue) return
    const end = newValue.toJSDate()
    const start = dateRange?.[0] || newValue.minus({ days: 7 }).startOf('day').toJSDate()
    setDateRange([start, end])
  }

  const handleSetLastWeek = () => {
    const end = dateRange[1]
    const start = DateTime.fromJSDate(end).minus({ days: 7 }).startOf('day').toJSDate()
    setDateRange([start, end])
  }

  const handleSetLast15Days = () => {
    const end = dateRange[1]
    const start = DateTime.fromJSDate(end).minus({ days: 15 }).startOf('day').toJSDate()
    setDateRange([start, end])
  }

  const handleSetLast30Days = () => {
    const end = dateRange[1]
    const start = DateTime.fromJSDate(end).minus({ days: 30 }).startOf('day').toJSDate()
    setDateRange([start, end])
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') onSearch()
  }

  return (
    <Stack spacing={1.5}>
      {/* Row 1: Search + Date range */}
      <Stack
        direction='row'
        alignItems='center'
        flexWrap='wrap'
        sx={{ gap: 1.5 }}
      >
        <TextField
          size='small'
          sx={{ width: 260 }}
          placeholder='Cerca nel contenuto...'
          value={contentSearch}
          onChange={(e) => setContentSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          aria-label='Ricerca contenuto file'
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon fontSize='small' color='action' />
                </InputAdornment>
              )
            }
          }}
        />

        <Autocomplete
          options={agencies || []}
          getOptionLabel={(option) => option?.name || ''}
          value={agencies?.find((a) => String(a._id) === String(agencyFilterId)) ?? null}
          onChange={(_, newValue) => setAgencyFilterId(newValue?._id != null ? newValue._id : null)}
          isOptionEqualToValue={(option, value) => option?._id === value?._id}
          size='small'
          sx={{ width: 220 }}
          renderOption={(props, option) => (
            <li {...props} key={option._id}>
              <Stack direction='row' alignItems='center' spacing={1}>
                <BusinessIcon fontSize='small' color='action' />
                <Typography variant='body2'>{option.name}</Typography>
              </Stack>
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label='Agenzia' placeholder='Tutte' />
          )}
        />

        <Divider orientation='vertical' flexItem sx={{ mx: 0.5 }} />

        <Stack direction='row' alignItems='center' sx={{ gap: 1 }}>
          <CalendarTodayIcon fontSize='small' color='action' />
          <Typography variant='body2' fontWeight={600} color='text.secondary'>
            Periodo
          </Typography>
          <DateTimePicker
            value={startDate}
            onChange={handleStartDateChange}
            format='dd/MM/yyyy HH:mm'
            ampm={false}
            slotProps={{
              textField: {
                size: 'small',
                sx: { width: 210 },
                placeholder: 'Da'
              }
            }}
          />
          <Typography variant='body2' color='text.secondary'>—</Typography>
          <DateTimePicker
            value={endDate}
            onChange={handleEndDateChange}
            format='dd/MM/yyyy HH:mm'
            ampm={false}
            slotProps={{
              textField: {
                size: 'small',
                sx: { width: 210 },
                placeholder: 'A'
              }
            }}
          />
          <Tooltip title='Imposta ultimi 7 giorni'>
            <Chip
              label='- 7 gg'
              size='small'
              variant='outlined'
              onClick={handleSetLastWeek}
              clickable
              aria-label='Imposta 7 giorni indietro'
            />
          </Tooltip>
          <Tooltip title='Imposta ultimi 15 giorni'>
            <Chip
              label='- 15 gg'
              size='small'
              variant='outlined'
              onClick={handleSetLast15Days}
              clickable
              aria-label='Imposta 15 giorni indietro'
            />
          </Tooltip>
          <Tooltip title='Imposta ultimi 30 giorni'>
            <Chip
              label='- 30 gg'
              size='small'
              variant='outlined'
              onClick={handleSetLast30Days}
              clickable
              aria-label='Imposta 30 giorni indietro'
            />
          </Tooltip>
        </Stack>
      </Stack>

      {/* Row 2: Filters + Actions */}
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        flexWrap='wrap'
        sx={{ gap: 1.5 }}
      >
        <Stack direction='row' alignItems='center' sx={{ gap: 1.5 }}>
          {/* <FormControlLabel
            control={
              <Switch
                checked={onlyErrors}
                onChange={(e) => setOnlyErrors(e.target.checked)}
                size='small'
                color='error'
              />
            }
            label={
              <Typography variant='body2' fontWeight={onlyErrors ? 600 : 400} color={onlyErrors ? 'error.main' : 'text.secondary'}>
                Solo errori
              </Typography>
            }
            sx={{
              ml: 0,
              border: '1px solid',
              borderColor: onlyErrors ? 'error.main' : 'divider',
              borderRadius: 2,
              px: 1.5,
              py: 0.25,
              backgroundColor: onlyErrors ? 'error.50' : 'transparent',
              transition: 'all 0.15s ease'
            }}
            aria-label='Mostra solo i file di errore'
          /> */}

          <FormControlLabel
            control={
              <Switch
                checked={!!onlyDuplicates}
                onChange={() => onToggleOnlyDuplicates?.()}
                size='small'
                color='info'
              />
            }
            label={
              <Typography variant='body2' fontWeight={onlyDuplicates ? 600 : 400} color={onlyDuplicates ? 'info.main' : 'text.secondary'}>
                Solo duplicati
              </Typography>
            }
            sx={{
              ml: 0,
              border: '1px solid',
              borderColor: onlyDuplicates ? 'info.main' : 'divider',
              borderRadius: 2,
              px: 1.5,
              py: 0.25,
              transition: 'all 0.15s ease'
            }}
            aria-label='Mostra solo i processi duplicati'
          />

          <Tooltip title={selectedRows?.length ? `Riprocessa ${selectedRows.length} selezionati` : 'Seleziona righe in errore, processate o inviate dalla tabella'}>
            <span>
              <Badge
                badgeContent={selectedRows?.length || 0}
                color='warning'
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Button
                  variant='outlined'
                  color='warning'
                  size='small'
                  disabled={!selectedRows?.length || reprocessing}
                  onClick={() => setReprocessConfirmOpen(true)}
                  startIcon={reprocessing ? <CircularProgress size={16} color='inherit' /> : <BuildIcon />}
                  aria-label='Riprocessa i file selezionati'
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                >
                  {reprocessing ? 'Riprocesso...' : 'Riprocessa'}
                </Button>
              </Badge>
            </span>
          </Tooltip>
        </Stack>

        <Stack direction='row' alignItems='center' sx={{ gap: 1 }}>
          <Tooltip title='Avvia ricerca con i filtri impostati'>
            <Button
              variant='contained'
              size='small'
              onClick={onSearch}
              startIcon={<SearchIcon />}
              aria-label='Avvia ricerca processi'
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Ricerca
            </Button>
          </Tooltip>
        </Stack>
      </Stack>

      <Dialog
        open={reprocessConfirmOpen}
        onClose={() => !reprocessing && setReprocessConfirmOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BuildIcon color='warning' />
          <Typography variant='h6' component='span' fontWeight={600}>
            Riprocessa file selezionati
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant='body1' sx={{ mb: 1 }}>
            Confermi di voler riprocessare <strong>{selectedCount}</strong>{' '}
            {selectedCount === 1 ? 'file selezionato' : 'file selezionati'}?
          </Typography>
          {previewFileNames.length > 0 && (
            <Stack component='ul' sx={{ m: 0, pl: 2.5, mb: 1 }}>
              {previewFileNames.map((name) => (
                <Typography key={name} component='li' variant='body2' sx={{ wordBreak: 'break-all' }}>
                  {name}
                </Typography>
              ))}
              {selectedCount > previewFileNames.length && (
                <Typography component='li' variant='body2' color='text.secondary'>
                  … e altri {selectedCount - previewFileNames.length}
                </Typography>
              )}
            </Stack>
          )}
          <Typography variant='body2' color='text.secondary'>
            Ogni file verrà elaborato di nuovo (tokenizzazione e/o invio FTP in base allo stato). L&apos;operazione può richiedere alcuni minuti.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            size='small'
            variant='outlined'
            onClick={() => setReprocessConfirmOpen(false)}
            disabled={reprocessing}
            sx={{ textTransform: 'none' }}
          >
            Annulla
          </Button>
          <Button
            size='small'
            variant='contained'
            color='warning'
            onClick={handleReprocessSelected}
            disabled={reprocessing}
            startIcon={reprocessing ? <CircularProgress size={16} color='inherit' /> : <BuildIcon />}
            sx={{ textTransform: 'none' }}
          >
            Riprocessa {selectedCount > 0 ? `(${selectedCount})` : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}
